import React, { useCallback, useEffect, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'
import { HiX, HiOutlineRefresh, HiOutlineMinus, HiOutlinePlus, HiOutlineExclamation } from 'react-icons/hi'
import { MdRotateRight } from 'react-icons/md'
import Skeleton from './Skeleton'
import { cropToBlob, readImage, toJpegFile } from '../util/imageFile'

const MAX_ZOOM = 4

// Full-screen crop dialog for uploads. `steps` is a list of frames to cut from the same photo (products: one 3:4
// frame; categories: a 3:4 tile, then a 16:9 wide frame). The frame always stays filled with photo, so the result
// never has blank space, and it is never stretched. onDone({ [step.key]: { file, url } }) with JPEG Files;
// onCancel(message?) — a message when the photo couldn't be used.
const ImageCropper = ({ file, steps, title = 'Crop photo', onCancel, onDone }) => {
    const [loaded, setLoaded] = useState(null) // { image, url, width, height }
    const [index, setIndex] = useState(0)
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [rotation, setRotation] = useState(0)
    const [area, setArea] = useState(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const results = useRef({})
    const dialogRef = useRef(null)
    const cancelRef = useRef(onCancel)
    cancelRef.current = onCancel

    const step = steps[index]
    const last = index === steps.length - 1

    useEffect(() => {
        let alive = true
        readImage(file)
            .then(result => { if (alive) setLoaded(result); else URL.revokeObjectURL(result.url) })
            .catch(e => cancelRef.current(e.message))
        return () => { alive = false }
    }, [file])

    useEffect(() => () => { if (loaded) URL.revokeObjectURL(loaded.url) }, [loaded])

    // lock page scroll (unless a side panel already did), Escape cancels, focus the dialog
    useEffect(() => {
        const root = document.documentElement
        const lockedBefore = root.classList.contains('mv-scroll-locked')
        if (!lockedBefore) root.classList.add('mv-scroll-locked')
        const returnFocus = document.activeElement
        const onKey = (e) => { if (e.key === 'Escape') cancelRef.current() }
        document.addEventListener('keydown', onKey)
        if (dialogRef.current) dialogRef.current.focus({ preventScroll: true })
        return () => {
            if (!lockedBefore) root.classList.remove('mv-scroll-locked')
            document.removeEventListener('keydown', onKey)
            if (returnFocus && returnFocus.focus && document.contains(returnFocus)) returnFocus.focus({ preventScroll: true })
        }
    }, [])

    const onCropComplete = useCallback((_, pixels) => setArea(pixels), [])

    const reset = () => {
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setRotation(0)
    }

    const back = () => {
        setIndex(i => i - 1)
        reset()
    }

    const next = () => {
        if (!loaded || !area) return
        setBusy(true)
        setError('')
        cropToBlob(loaded.image, area, rotation, step.width, step.height)
            .then(blob => {
                results.current[step.key] = { file: toJpegFile(blob, file.name), url: URL.createObjectURL(blob) }
                if (last) {
                    onDone({ ...results.current })
                } else {
                    setIndex(i => i + 1)
                    reset()
                    setBusy(false)
                }
            })
            .catch(e => {
                setBusy(false)
                setError(e.message || 'Could not prepare the photo. Please try again.')
            })
    }

    const lowRes = area && area.width < step.minWidth

    return (
        <div className='mv-cropper-overlay'>
            <div className='mv-cropper' role='dialog' aria-modal='true' aria-labelledby='mv-cropper-title' tabIndex={-1} ref={dialogRef}>
                <header className='mv-cropper-head'>
                    <div>
                        <h2 id='mv-cropper-title' className='mv-cropper-title'>{title}</h2>
                        <p className='mv-cropper-sub'>
                            {steps.length > 1 && <span className='mv-cropper-step'>Step {index + 1} of {steps.length}</span>}
                            {step.label} · {step.aspect === 3 / 4 ? '3:4 portrait' : step.aspect === 16 / 9 ? '16:9 wide' : 'fixed shape'}
                            {step.hint ? ' · ' + step.hint : ''}
                        </p>
                    </div>
                    <button type='button' className='mv-icon-btn mv-cropper-close' aria-label='Cancel' onClick={() => onCancel()}>
                        <HiX />
                    </button>
                </header>

                <div className='mv-cropper-stage'>
                    {loaded ?
                        <Cropper
                            key={step.key}
                            image={loaded.url}
                            crop={crop}
                            zoom={zoom}
                            rotation={rotation}
                            aspect={step.aspect}
                            maxZoom={MAX_ZOOM}
                            zoomSpeed={0.25}
                            showGrid
                            objectFit='contain'
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onRotationChange={setRotation}
                            onCropComplete={onCropComplete}
                        /> :
                        <Skeleton className='mv-cropper-skeleton' height='100%' />}
                </div>

                <div className='mv-cropper-controls'>
                    <div className='mv-cropper-zoom'>
                        <button type='button' className='mv-icon-btn' aria-label='Zoom out' disabled={!loaded || zoom <= 1}
                            onClick={() => setZoom(z => Math.max(1, Math.round((z - 0.25) * 100) / 100))}>
                            <HiOutlineMinus />
                        </button>
                        <input type='range' className='mv-cropper-range' min={1} max={MAX_ZOOM} step={0.01} value={zoom}
                            aria-label='Zoom' disabled={!loaded} onChange={e => setZoom(Number(e.target.value))} />
                        <button type='button' className='mv-icon-btn' aria-label='Zoom in' disabled={!loaded || zoom >= MAX_ZOOM}
                            onClick={() => setZoom(z => Math.min(MAX_ZOOM, Math.round((z + 0.25) * 100) / 100))}>
                            <HiOutlinePlus />
                        </button>
                    </div>
                    <div className='mv-cropper-tools'>
                        <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm' disabled={!loaded}
                            onClick={() => setRotation(r => (r + 90) % 360)}>
                            <MdRotateRight aria-hidden='true' /> Rotate
                        </button>
                        <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm' disabled={!loaded} onClick={reset}>
                            <HiOutlineRefresh aria-hidden='true' /> Reset
                        </button>
                    </div>
                    <p className='mv-cropper-help'>Drag to move · pinch, scroll or use the slider to zoom</p>
                </div>

                {lowRes &&
                    <p className='mv-cropper-warn' role='status'>
                        <HiOutlineExclamation aria-hidden='true' /> This area is small ({Math.round(area.width)}×{Math.round(area.height)} px) and may look blurry. Zoom out or use a larger photo.
                    </p>}
                {error && <div className='mv-alert mv-alert-error' role='alert'>{error}</div>}

                <footer className='mv-cropper-foot'>
                    {index > 0 ?
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={back} disabled={busy}>Back</button> :
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={() => onCancel()} disabled={busy}>Cancel</button>}
                    <button type='button' className='mv-btn mv-btn-primary' onClick={next} disabled={!loaded || !area || busy}>
                        {busy ? 'Preparing…' : last ? 'Use photo' : 'Next'}
                    </button>
                </footer>
            </div>
        </div>
    )
}

export default ImageCropper
